import {Injectable} from '@angular/core';
import {environment} from 'environments/environment';
import {BehaviorSubject, Observable} from 'rxjs';
import {KeycloakEventType, KeycloakService} from 'keycloak-angular';

export interface TokenPayload {
  exp?: number;
  iat?: number;
  auth_time?: number;
  jti?: string;
  iss?: string;
  aud?: string | string[];
  sub?: string;
  typ?: string;
  azp?: string;
  session_state?: string;
  acr?: string;
  realm_access?: { roles: string[] };
  resource_access?: { [clientId: string]: { roles: string[] } };
  preferred_username?: string;
  email?: string;
  name?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth$ = new BehaviorSubject<boolean>(false);
  private readonly refreshSkewSeconds = 60;

  constructor(private keycloak: KeycloakService) {
    this.syncAuthState();
    this.subscribeToKeycloakEvents();
  }

  isAuthenticated$(): Observable<boolean> {
    return this.auth$.asObservable();
  }

  async isAuthenticated(): Promise<boolean> {
    return this.keycloak.isLoggedIn();
  }

  get accessToken(): string | null {
    return this.keycloak.getKeycloakInstance().token ?? null;
  }

  get tokenPayload(): TokenPayload | null {
    const parsed = this.keycloak.getKeycloakInstance().tokenParsed as TokenPayload | undefined;
    return parsed ?? null;
  }

  get clientRoles(): string[] {
    const payload = this.tokenPayload;
    const client = environment.ssoClientId;
    const roles = payload?.resource_access?.[client]?.roles ?? [];
    return Array.isArray(roles) ? roles : [];
  }

  get realmRoles(): string[] {
    const payload = this.tokenPayload;
    const roles = payload?.realm_access?.roles ?? [];
    return Array.isArray(roles) ? roles : [];
  }

  get allRoles(): string[] {
    return [...new Set([...this.clientRoles, ...this.realmRoles])];
  }

  hasPermission(permission: string): boolean {
    return this.hasRole(permission);
  }

  hasAnyPermission(permissions: string | string[]): boolean {
    return this.hasAnyRole(permissions);
  }

  hasRole(role: string): boolean {
    const requiredRole = this.normalizeAccessValue(role);
    return this.allRoles.some((userRole) => this.normalizeAccessValue(userRole) === requiredRole);
  }

  hasAnyRole(roles: string | string[]): boolean {
    const requiredRoles = this.toList(roles);
    return requiredRoles.length === 0 || requiredRoles.some((role) => this.hasRole(role));
  }

  hasAccess(access: { roles?: string | string[]; permissions?: string | string[] }): boolean {
    const roles = this.toList(access.roles);
    const permissions = this.toList(access.permissions);
    const requiredRoles = roles.length > 0 ? roles : permissions;
    return this.hasAnyRole(requiredRoles);
  }

  isSelfServiceProfile(): boolean {
    const normalizedRoles = this.allRoles.map((role) => this.normalizeAccessValue(role));
    return normalizedRoles.some((role) => role === 'selfservice');
  }

  async login(redirectUrl?: string): Promise<void> {
    const redirectUri = this.buildRedirectUri(redirectUrl);
    await this.keycloak.login({ redirectUri });
    this.auth$.next(true);
  }

  async loginWithRedirect(redirectUrl?: string): Promise<void> {
    return this.login(redirectUrl);
  }

  async refresh(): Promise<void> {
    await this.keycloak.updateToken(this.refreshSkewSeconds);
  }

  async forceRefresh(): Promise<void> {
    await this.keycloak.updateToken(-1);
  }

  async logout(): Promise<void> {
    const redirectUri = this.buildRedirectUri('/sign-in');
    await this.keycloak.logout(redirectUri);
    this.auth$.next(false);
  }

  async logoutRemote(): Promise<void> {
    await this.logout();
  }

  private syncAuthState(): void {
    Promise.resolve(this.keycloak.isLoggedIn())
      .then((logged: boolean) => this.auth$.next(logged))
      .catch(() => this.auth$.next(false));
  }

  private buildRedirectUri(path?: string): string {
    const target = path || '/dashboard';
    const normalized = target.startsWith('/') ? target : `/${target}`;
    return `${window.location.origin}/#${normalized}`;
  }

  private normalizeAccessValue(value: string): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  private toList(value: string | string[] | null | undefined): string[] {
    if (!value) {
      return [];
    }

    const values = Array.isArray(value) ? value : value.split('|');
    return values.map((item) => item.trim()).filter(Boolean);
  }

  private subscribeToKeycloakEvents(): void {
      this.keycloak.keycloakEvents$.subscribe((event) => {
          this.handleKeycloakEvent(event.type);
      });
  }

  private handleKeycloakEvent(eventType: KeycloakEventType): void {
      switch (eventType) {
          case KeycloakEventType.OnAuthSuccess:
          case KeycloakEventType.OnAuthRefreshSuccess:
              this.syncAuthState();
              break;
          case KeycloakEventType.OnTokenExpired:
              this.refresh().catch(() => this.logout());
              break;
          case KeycloakEventType.OnAuthRefreshError:
          case KeycloakEventType.OnAuthLogout:
          case KeycloakEventType.OnAuthError:
              this.auth$.next(false);
              void this.logout();
              break;
        }
    }
}
